import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import { useTranslation } from "react-i18next";
import {
  DoraButton,
  DoraCheckbox,
  UFDoraSelect,
  DoraTextInput,
  UFDoraTextInput,
} from "dora-shared/frontend";
import { useAppDispatch, useSelector } from "../../../../../redux-store";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  addEconomicClient,
  updateEconomicClient,
  getCustomerGroups,
  getEconomicClientForEdit,
} from "../../../../../ducks/data/clients";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { selectClients } from "../../../../../ducks/data/clients/selectors";
import axios from "../../../../../axios";
import { notifyL } from "../../../../../ducks/notifications";
import { CustomerGroup } from "dora-contracts";
import {
  ECONOMIC_MAX_CUSTOMER_NUMBER,
  ECONOMIC_CUSTOMER_EMAIL_MAX_LENGTH,
} from "dora-shared";
import {
  ServerRejection,
  serverRejectionText,
} from "../../../../../ducks/helpers";
import { serverErrorText } from "../../../../../i18n/server-error-text";

const AddEditEconomicCustomerDialog = ({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["settings", "components", "notifications"]);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [searchRequestInProgress, setSearchRequestInProgress] = useState(false);
  const [loadingEditData, setLoadingEditData] = useState(!!id);
  // Why the customer can't be edited at all, e.g. it no longer exists in
  // e-conomic. Shown in place of the form.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const dispatch = useAppDispatch();
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[] | null>(
    null
  );

  const clients = useSelector(selectClients);

  const clientsNumbers = useMemo(() => {
    const numbers = clients.map((c) => c.clientNumber);
    if (id) {
      const currentClient = clients.find((c) => c.id === id);
      if (currentClient) {
        return numbers.filter((n) => n !== currentClient.clientNumber);
      }
    }
    return numbers;
  }, [clients, id]);

  const maxClientNumber = useMemo(() => {
    if (clientsNumbers.length === 0) return "1";
    const numbers = clientsNumbers
      .map((n) => parseInt(n, 10))
      .filter((n) => !isNaN(n));
    return numbers.length > 0 ? String(Math.max(...numbers) + 1) : "1";
  }, [clientsNumbers]);

  useEffect(() => {
    dispatch(getCustomerGroups())
      .unwrap()
      .then(setCustomerGroups)
      .catch(() => {
        // The getCustomerGroups thunk has already reported it and shown the error toast.
      });
  }, [dispatch]);

  const customerGroupsOptions = useMemo(
    () =>
      customerGroups
        ? customerGroups.map((cg) => ({
            value: cg.customerGroupNumber.toString(),
            label: cg.name,
          }))
        : [],
    [customerGroups]
  );

  const formMethods = useForm({
    defaultValues: {
      name: "",
      cvrNumber: "",
      address: "",
      postcode: "",
      city: "",
      country: "",
      email: "",
      phoneNumberMobile: "",
      clientNumber: maxClientNumber,
      customerGroupNumber: null,
      visibleToDrivers: true,
    },
    mode: "onChange",
    reValidateMode: "onChange",
    criteriaMode: "all",
    resolver: yupResolver(
      yup.object().shape({
        name: yup.string().required(),
        clientNumber: yup
          .string()
          .matches(
            /^(\d+)?$/,
            t(
              "settings:customersConfiguration.addCustomerDialog.validation.onlyDigits"
            )
          )
          .test(
            "economic-range",
            t(
              "settings:customersConfiguration.addCustomerDialog.validation.numberOutOfRange",
              { max: ECONOMIC_MAX_CUSTOMER_NUMBER }
            ),
            // Empty is left to .required(), non-digits to .matches() above.
            (value) =>
              !value ||
              !/^\d+$/.test(value) ||
              (Number(value) >= 1 &&
                Number(value) <= ECONOMIC_MAX_CUSTOMER_NUMBER)
          )
          .required()
          .notOneOf(
            clientsNumbers,
            t(
              "settings:customersConfiguration.addCustomerDialog.validation.clientNumberExists"
            )
          ),
        cvrNumber: yup.string().optional(),
        address: yup.string().optional(),
        postcode: yup.string().optional(),
        city: yup.string().optional(),
        country: yup.string().optional(),
        email: yup
          .string()
          .optional()
          .max(
            ECONOMIC_CUSTOMER_EMAIL_MAX_LENGTH,
            t(
              "settings:customersConfiguration.addCustomerDialog.validation.emailTooLong",
              { max: ECONOMIC_CUSTOMER_EMAIL_MAX_LENGTH }
            )
          ),
        phoneNumberMobile: yup.string().optional(),
        customerGroupNumber: yup.string().required(),
        visibleToDrivers: yup.boolean().required(),
      }),
      { abortEarly: false }
    ),
  });

  const {
    register,
    getValues,
    formState,
    watch,
    handleSubmit,
    setValue,
    control,
    trigger,
  } = formMethods;

  useEffect(() => {
    if (id) {
      dispatch(getEconomicClientForEdit(id))
        .then((result) => {
          if (getEconomicClientForEdit.rejected.match(result)) {
            // Without a payload the thunk has already reported it and shown
            // the error toast.
            if (result.payload) {
              setLoadError(
                serverRejectionText(
                  result.payload,
                  serverErrorText,
                  t("notifications:actionFailed")
                )
              );
            }
            return;
          }
          const data = result.payload;
          setValue("name", data.name);
          setValue("cvrNumber", data.cvrNumber ?? "");
          setValue("address", data.address ?? "");
          setValue("postcode", data.postcode ?? "");
          setValue("city", data.city ?? "");
          setValue("country", data.country ?? "");
          setValue("email", data.email ?? "");
          setValue("phoneNumberMobile", data.phoneNumberMobile ?? "");
          setValue("clientNumber", data.clientNumber);
          if (data.customerGroupNumber !== null) {
            setValue(
              "customerGroupNumber",
              String(data.customerGroupNumber) as any
            );
          }
          setValue("visibleToDrivers", data.visibleToDrivers);
          void trigger();
        })
        .finally(() => setLoadingEditData(false));
    }
  }, [id, dispatch, setValue, trigger, t]);

  const nameWatch = watch("name");

  const nameExists = useMemo(
    () => clients.find((c) => c.client === nameWatch && c.id !== id),
    [clients, nameWatch, id]
  );

  useEffect(() => {
    if (customerGroupsOptions.length) {
      setValue(
        "customerGroupNumber",
        String(customerGroupsOptions[0].value) as any
      );
    }
  }, [customerGroupsOptions, setValue]);

  const { isValid, errors } = formState;

  // A refusal the server aimed at the user is shown in the dialog so they can
  // fix it and retry; any other failure has already been reported and toasted
  // by the thunk.
  const showRejection = (rejection: ServerRejection | undefined) => {
    if (rejection) {
      setServerError(
        serverRejectionText(
          rejection,
          serverErrorText,
          t("notifications:actionFailed")
        )
      );
    }
  };

  const onCreateClick = () => {
    handleSubmit(
      () => {
        setRequestInProgress(true);
        setServerError(null);
        const values = getValues();
        dispatch(
          addEconomicClient({
            ...values,
            customerGroupNumber: Number(values.customerGroupNumber!),
          })
        )
          .then((result) => {
            if (addEconomicClient.rejected.match(result)) {
              showRejection(result.payload);
            } else {
              onClose();
            }
          })
          .finally(() => {
            setRequestInProgress(false);
          });
      },
      (err) => {
        console.error(err);
        setRequestInProgress(false);
      }
    )();
  };

  const onUpdateClick = () => {
    handleSubmit(
      () => {
        setRequestInProgress(true);
        setServerError(null);
        const values = getValues();
        dispatch(
          updateEconomicClient({
            clientId: id!,
            name: values.name,
            cvrNumber: values.cvrNumber,
            address: values.address,
            postcode: values.postcode,
            city: values.city,
            country: values.country,
            email: values.email,
            phoneNumberMobile: values.phoneNumberMobile,
            customerGroupNumber: Number(values.customerGroupNumber!),
          })
        )
          .then((result) => {
            if (updateEconomicClient.rejected.match(result)) {
              showRejection(result.payload);
            } else {
              onClose();
            }
          })
          .finally(() => {
            setRequestInProgress(false);
          });
      },
      (err) => {
        console.error(err);
        setRequestInProgress(false);
      }
    )();
  };

  const onSearchClick = (e: any) => {
    e.preventDefault();
    setSearchRequestInProgress(true);
    const errNotif = notifyL({
      type: "error",
      key: "actionFailed",
      namespace: "notifications",
    });
    try {
      axios
        .get(`https://cvrapi.dk/api?search=${searchValue}&country=dk`)
        .then((res) => {
          if (res.data) {
            const { name, vat, address, zipcode, city, email, phone } =
              res.data;
            setSearchValue("");
            setValue("name", name || "");
            setValue("cvrNumber", vat ? String(vat) : "");
            setValue("address", address || "");
            setValue("postcode", zipcode ? String(zipcode) : "");
            setValue("city", city || "");
            setValue("email", email || "");
            setValue("phoneNumberMobile", phone ? String(phone) : "");
            trigger();
          }
        })
        .catch((err) => {
          dispatch(errNotif);
          console.error(err);
        })
        .finally(() => setSearchRequestInProgress(false));
    } catch (err) {
      console.error(err);
      dispatch(errNotif);
      setSearchRequestInProgress(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth={"lg"}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (isValid && !requestInProgress) {
            if (id) {
              onUpdateClick();
            } else {
              onCreateClick();
            }
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h2">
            {id
              ? t("settings:customersConfiguration.addCustomerDialog.titleEdit")
              : t("settings:customersConfiguration.addCustomerDialog.title")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {loadingEditData ? (
            <div className="flex items-center justify-center py-12 px-24">
              <CircularProgress size={32} />
            </div>
          ) : loadError !== null ? (
            <Typography color="error">{loadError}</Typography>
          ) : (
            <>
              {id && (
                <div className="mt-2 mb-6 flex gap-2 items-center w-full">
                  <DoraTextInput
                    style={{ minWidth: 430 }}
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.searchPlaceholder"
                    )}
                    value={searchValue}
                    onChange={setSearchValue}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (searchValue && !searchRequestInProgress) {
                          onSearchClick(e);
                        }
                      }
                    }}
                  />
                  <DoraButton
                    variant="primary-filled"
                    type="submit"
                    disabled={!searchValue || searchRequestInProgress}
                    onClick={onSearchClick}
                  >
                    {t("components:buttonLabels.search")}
                  </DoraButton>
                </div>
              )}
              <div className="grid gap-3 grid-cols-2">
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.name"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.name"
                    )}
                    register={register("name")}
                  />
                  {nameExists && (
                    <div
                      className="mt-1"
                      style={{ color: "var(--warning-500)" }}
                    >
                      {t(
                        "settings:customersConfiguration.addCustomerDialog.warnNameExists"
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.orgNumber"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.cvrNumber"
                    )}
                    register={register("cvrNumber")}
                  />
                </div>
              </div>
              <div className="mt-3 grid gap-3 grid-cols-2">
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.clientNumber"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.clientNumber"
                    )}
                    register={register("clientNumber")}
                  />
                  {errors.clientNumber &&
                    errors.clientNumber.type !== "required" && (
                      <div
                        className="mt-1"
                        style={{ color: "var(--error-500)" }}
                      >
                        {errors.clientNumber.message}
                      </div>
                    )}
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.customerGroup"
                    )}
                  </label>
                  {!customerGroups && <div>Loading...</div>}
                  <div>
                    <UFDoraSelect
                      className="w-full"
                      fullWidth
                      style={{
                        visibility: customerGroups ? "visible" : "hidden",
                      }}
                      options={customerGroupsOptions}
                      name={"customerGroupNumber"}
                      control={control}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-3 grid gap-3 grid-cols-3">
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.address"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.address"
                    )}
                    register={register("address")}
                  />
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.postcode"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.postcode"
                    )}
                    register={register("postcode")}
                  />
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.city"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.city"
                    )}
                    register={register("city")}
                  />
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.country"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.country"
                    )}
                    register={register("country")}
                  />
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.email"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.email"
                    )}
                    register={register("email")}
                  />
                  {errors.email && (
                    <div className="mt-1" style={{ color: "var(--error-500)" }}>
                      {errors.email.message}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.mobilePhone"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.mobilePhone"
                    )}
                    register={register("phoneNumberMobile")}
                  />
                </div>
              </div>

              {!id && (
                <div className="flex items-center gap-2 mt-4">
                  <DoraCheckbox register={register("visibleToDrivers")} />
                  <label className="text-sm">
                    {t(
                      "settings:customersConfiguration.customerPage.visibleToDriversCheckBox"
                    )}
                  </label>
                </div>
              )}
            </>
          )}
          {serverError && (
            <Typography className="mt-4" color="error">
              {serverError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <div className="flex gap-2 justify-end items-center">
            <DoraButton type="button" variant={"primary"} onClick={onClose}>
              {t("components:buttonLabels.cancel")}
            </DoraButton>
            <DoraButton
              type="submit"
              variant={"primary-filled"}
              disabled={requestInProgress || !isValid || loadError !== null}
            >
              {t("components:buttonLabels.save")}
            </DoraButton>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddEditEconomicCustomerDialog;
